import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePin } from '../contexts/PinContext';
import { AppLayout } from '../components/Layout';
import { Button, DeletionConfirmModal, DataExportModal, DataImportModal } from '../components/UI';
import TwoFactorSettings from '../components/Security/TwoFactorSettings';
import PinManagementSection from '../components/Settings/PinManagementSection';
import PinMigrationBanner from '../components/Settings/PinMigrationBanner';
import { exportService } from '../services/exportService';
import { importService } from '../services/importService';
import { 
  Shield, 
  Moon, 
  Sun, 
  Globe, 
  Bell, 
  Lock,
  Trash2,
  Download,
  Upload,
  Key,
  Eye,
  EyeOff,
  Save,
  AlertCircle
} from 'lucide-react';
import './SettingsPage.css';

function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    theme: localStorage.getItem('theme') || 'light',
    language: localStorage.getItem('language') || 'ko',
    notifications: localStorage.getItem('notifications') === 'true',
    autoLock: localStorage.getItem('autoLock') || '15',
    passwordVisibility: localStorage.getItem('passwordVisibility') === 'true'
  });
  const [saveStatus, setSaveStatus] = useState('');
  const [showDataDeleteModal, setShowDataDeleteModal] = useState(false);
  const [showAccountDeleteModal, setShowAccountDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    // 즉시 적용되는 설정들
    if (key === 'theme') {
      document.documentElement.setAttribute('data-theme', value);
      localStorage.setItem('theme', value);
    } else if (key === 'language') {
      localStorage.setItem('language', value);
    } else if (key === 'notifications') {
      localStorage.setItem('notifications', value);
    } else if (key === 'autoLock') {
      localStorage.setItem('autoLock', value);
    } else if (key === 'passwordVisibility') {
      localStorage.setItem('passwordVisibility', value);
    }
    
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const handleExportData = async (exportConfig) => {
    setExportLoading(true);
    try {
      await exportService.exportUserData(user.uid, exportConfig);
      setShowExportModal(false);
      alert('데이터 내보내기가 완료되었습니다.');
    } catch (error) {
      console.error('Export error:', error);
      alert('데이터 내보내기 중 오류가 발생했습니다.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportData = async (importConfig) => {
    setImportLoading(true);
    try {
      const result = await importService.importUserData(user.uid, importConfig);
      
      setShowImportModal(false);
      
      // 결과 메시지 생성
      const totalImported = Object.values(result.imported).reduce((sum, count) => sum + count, 0);
      const totalSkipped = Object.values(result.skipped).reduce((sum, count) => sum + count, 0);
      
      let message = `데이터 가져오기가 완료되었습니다.\n\n`;
      message += `✅ 가져온 항목: ${totalImported}개\n`;
      if (totalSkipped > 0) {
        message += `⏭️ 건너뛴 항목: ${totalSkipped}개\n`;
      }
      
      if (result.imported.passwords > 0) message += `- 패스워드: ${result.imported.passwords}개\n`;
      if (result.imported.creditCards > 0) message += `- 신용카드: ${result.imported.creditCards}개\n`;
      if (result.imported.apiKeys > 0) message += `- API 키: ${result.imported.apiKeys}개\n`;
      if (result.imported.secureNotes > 0) message += `- 보안 메모: ${result.imported.secureNotes}개\n`;
      if (result.imported.attachments > 0) message += `- 첨부파일: ${result.imported.attachments}개\n`;
      
      if (result.errors.length > 0) {
        message += `\n⚠️ 오류가 발생한 항목:\n`;
        result.errors.slice(0, 5).forEach(error => {
          message += `- ${error}\n`;
        });
        if (result.errors.length > 5) {
          message += `- 그 외 ${result.errors.length - 5}개 오류\n`;
        }
      }
      
      alert(message);
      
      // 페이지 새로고침으로 UI 업데이트
      if (totalImported > 0) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('데이터 가져오기 중 오류가 발생했습니다.\n' + error.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleDeleteDataConfirm = async () => {
    setDeleteLoading(true);
    try {
      // 데이터 삭제 로직 구현
      await new Promise(resolve => setTimeout(resolve, 2000)); // 임시 지연
      alert('모든 데이터가 삭제되었습니다.\n새로운 시작을 위해 페이지를 새로고침합니다.');
      window.location.reload();
    } catch (error) {
      console.error('Delete data error:', error);
      alert('데이터 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(false);
      setShowDataDeleteModal(false);
    }
  };

  const handleDeleteAccountConfirm = async () => {
    setDeleteLoading(true);
    try {
      // 계정 완전 삭제 로직 구현
      await new Promise(resolve => setTimeout(resolve, 2000)); // 임시 지연
      alert('계정이 완전히 삭제되었습니다.\n이용해주셔서 감사했습니다.');
      // 로그아웃 처리
      window.location.href = '/';
    } catch (error) {
      console.error('Delete account error:', error);
      alert('계정 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(false);
      setShowAccountDeleteModal(false);
    }
  };

  const tabs = [
    { id: 'general', label: '일반', icon: Globe },
    { id: 'security', label: '보안', icon: Shield },
    { id: 'pin', label: 'PIN 관리', icon: Key },
    { id: 'data', label: '데이터', icon: Download },
    { id: 'account', label: '계정', icon: Lock }
  ];

  return (
    <AppLayout 
      pageTitle="설정" 
      pageSubtitle="애플리케이션 설정을 관리합니다"
    >
      <div className="settings-page">
        <div className="settings-container">
          {/* 탭 네비게이션 */}
          <div className="settings-tabs">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* 설정 콘텐츠 */}
          <div className="settings-content">
            {saveStatus && (
              <div className="settings-save-status">
                ✅ 설정이 저장되었습니다
              </div>
            )}

            {/* 일반 설정 */}
            {activeTab === 'general' && (
              <div className="settings-section">
                <h2 className="settings-section-title">일반 설정</h2>
                
                <div className="setting-item">
                  <div className="setting-label">
                    <span>테마</span>
                    <p className="setting-description">애플리케이션 테마를 선택합니다</p>
                  </div>
                  <div className="setting-control">
                    <div className="theme-selector">
                      <button
                        className={`theme-option ${settings.theme === 'light' ? 'active' : ''}`}
                        onClick={() => handleSettingChange('theme', 'light')}
                      >
                        <Sun size={16} />
                        <span>라이트</span>
                      </button>
                      <button
                        className={`theme-option ${settings.theme === 'dark' ? 'active' : ''}`}
                        onClick={() => handleSettingChange('theme', 'dark')}
                      >
                        <Moon size={16} />
                        <span>다크</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-label">
                    <span>언어</span>
                    <p className="setting-description">표시 언어를 선택합니다</p>
                  </div>
                  <div className="setting-control">
                    <select 
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      className="setting-select"
                    >
                      <option value="ko">한국어</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-label">
                    <span>알림</span>
                    <p className="setting-description">브라우저 알림을 허용합니다</p>
                  </div>
                  <div className="setting-control">
                    <label className="setting-switch">
                      <input
                        type="checkbox"
                        checked={settings.notifications}
                        onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* 보안 설정 */}
            {activeTab === 'security' && (
              <div className="settings-section">
                <h2 className="settings-section-title">보안 설정</h2>
                
                <div className="setting-item setting-item-full">
                  <TwoFactorSettings />
                </div>

                <div className="setting-item">
                  <div className="setting-label">
                    <span>자동 잠금</span>
                    <p className="setting-description">비활성 시 자동으로 잠금</p>
                  </div>
                  <div className="setting-control">
                    <select 
                      value={settings.autoLock}
                      onChange={(e) => handleSettingChange('autoLock', e.target.value)}
                      className="setting-select"
                    >
                      <option value="5">5분</option>
                      <option value="15">15분</option>
                      <option value="30">30분</option>
                      <option value="60">1시간</option>
                      <option value="never">사용 안 함</option>
                    </select>
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-label">
                    <span>패스워드 표시</span>
                    <p className="setting-description">기본적으로 패스워드를 표시합니다</p>
                  </div>
                  <div className="setting-control">
                    <label className="setting-switch">
                      <input
                        type="checkbox"
                        checked={settings.passwordVisibility}
                        onChange={(e) => handleSettingChange('passwordVisibility', e.target.checked)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* PIN 관리 */}
            {activeTab === 'pin' && (
              <div className="settings-section">
                <h2 className="settings-section-title">PIN 관리</h2>
                
                {/* PIN 시스템 마이그레이션 배너 */}
                <PinMigrationBanner />
                
                <PinManagementSection />
              </div>
            )}

            {/* 데이터 관리 */}
            {activeTab === 'data' && (
              <div className="settings-section">
                <h2 className="settings-section-title">데이터 관리</h2>
                
                <div className="setting-item">
                  <div className="setting-label">
                    <span>데이터 내보내기</span>
                    <p className="setting-description">패스워드, 신용카드, API키, 보안메모, 첨부파일을 선택적으로 내보냅니다</p>
                  </div>
                  <div className="setting-control">
                    <Button
                      variant="secondary"
                      onClick={() => setShowExportModal(true)}
                    >
                      <Download size={16} />
                      내보내기
                    </Button>
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-label">
                    <span>데이터 가져오기</span>
                    <p className="setting-description">JSON 파일에서 패스워드 데이터를 가져옵니다</p>
                  </div>
                  <div className="setting-control">
                    <Button
                      variant="secondary"
                      onClick={() => setShowImportModal(true)}
                    >
                      <Upload size={16} />
                      가져오기
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* 계정 설정 */}
            {activeTab === 'account' && (
              <div className="settings-section">
                <h2 className="settings-section-title">계정 정보</h2>
                
                <div className="setting-item">
                  <div className="setting-label">
                    <span>이메일</span>
                    <p className="setting-description">현재 로그인된 계정</p>
                  </div>
                  <div className="setting-control">
                    <span className="account-email">{user?.email}</span>
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-label">
                    <span>가입일</span>
                    <p className="setting-description">계정 생성일</p>
                  </div>
                  <div className="setting-control">
                    <span className="account-date">
                      {user?.metadata?.creationTime ? 
                        new Date(user.metadata.creationTime).toLocaleDateString('ko-KR') : 
                        '-'
                      }
                    </span>
                  </div>
                </div>

                <div className="settings-danger-zone">
                  <h3 className="danger-title">
                    <AlertCircle size={18} />
                    위험 구역
                  </h3>
                  
                  <div className="setting-item danger">
                    <div className="setting-label">
                      <span>모든 데이터 삭제</span>
                      <p className="setting-description text-danger">
                        패스워드, 보안 메모, 공유 링크 등 모든 데이터를 삭제합니다.<br />
                        계정은 유지되며 다시 로그인할 수 있습니다.
                      </p>
                    </div>
                    <div className="setting-control">
                      <Button
                        variant="danger"
                        onClick={() => setShowDataDeleteModal(true)}
                      >
                        <Trash2 size={16} />
                        데이터 삭제
                      </Button>
                    </div>
                  </div>

                  <div className="setting-item danger">
                    <div className="setting-label">
                      <span>계정 완전 삭제</span>
                      <p className="setting-description text-danger">
                        모든 데이터 삭제 + Google 계정 연결 해제<br />
                        완전히 삭제되며 다시 가입해야 합니다.
                      </p>
                    </div>
                    <div className="setting-control">
                      <Button
                        variant="danger"
                        onClick={() => setShowAccountDeleteModal(true)}
                      >
                        <Trash2 size={16} />
                        계정 삭제
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 데이터 삭제 확인 모달 */}
      <DeletionConfirmModal
        isOpen={showDataDeleteModal}
        onClose={() => setShowDataDeleteModal(false)}
        onConfirm={handleDeleteDataConfirm}
        title="모든 데이터 삭제"
        description="정말로 모든 데이터를 삭제하시겠습니까? 패스워드, 보안 메모, 공유 링크, 2FA 설정 등 모든 데이터가 영구적으로 삭제됩니다. 계정은 유지되며 다시 로그인할 수 있습니다."
        confirmationText="데이터 삭제"
        buttonText="데이터 삭제"
        loading={deleteLoading}
      />

      {/* 계정 삭제 확인 모달 */}
      <DeletionConfirmModal
        isOpen={showAccountDeleteModal}
        onClose={() => setShowAccountDeleteModal(false)}
        onConfirm={handleDeleteAccountConfirm}
        title="계정 완전 삭제"
        description="정말로 계정을 완전히 삭제하시겠습니까? 모든 데이터가 삭제되고 Google 계정 연결이 해제됩니다. 이후 다시 가입해야 하며, 이 작업은 되돌릴 수 없습니다."
        confirmationText="계정 완전 삭제"
        buttonText="계정 삭제"
        loading={deleteLoading}
      />

      {/* 데이터 내보내기 모달 */}
      <DataExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportData}
        loading={exportLoading}
      />

      {/* 데이터 가져오기 모달 */}
      <DataImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportData}
        loading={importLoading}
      />
    </AppLayout>
  );
}

export default SettingsPage;